import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  BulkUpdatePayloadType,
  McrContactType,
  MutationType,
  RuleType,
} from '../../../shared/types';
import request from '../../../utils/request';
import i18n from '../../../i18n';

export const useGetContacts = ({
  per_page = 10,
  page = 1,
}: {
  per_page?: number;
  page?: number;
}) => {
  return useQuery('contacts', async () => {
    const data = await request(`/contacts/?per_page=${per_page}&page=${page}`);
    return data;
  });
};

export const useGetContact = (
  contactId: string,
  { callback }: MutationType = {},
) => {
  return useQuery(
    ['contact', contactId],
    async () => {
      const data: McrContactType = await request(`/contacts/${contactId}`);
      return data;
    },
    {
      enabled: !!contactId,
      onSuccess: (response: McrContactType) => {
        if (callback) callback(true, response);
      },
    },
  );
};

export const useSaveContact = ({
  setToast,
  toggleDrawer,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      payload: contact,
    }: {
      payload: McrContactType;
      touchedFields?: any;
    }) => {
      const data = await request(
        contact.id ? `/contacts/${contact.id}` : '/contacts/',
        contact,
        contact.id ? 'PATCH' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response, { payload: contact, touchedFields }) => {
        queryClient.setQueryData(['contact', `${response.id}`], response);
        if (touchedFields && touchedFields.list_ids) {
          queryClient.invalidateQueries('contact-lists');
        }
        if (toggleDrawer) toggleDrawer(false);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: contact.id
              ? `${i18n.t('forms.toast.success.contact_updated')}`
              : `${i18n.t('forms.toast.success.contact_created')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useDeleteContact = ({
  setToast,
  toggleDrawer,
  callback,
}: MutationType) => {
  return useMutation(
    async (contactId: string) => {
      const data = await request(`/contacts/${contactId}`, {}, 'DELETE');
      return data;
    },
    {
      onSuccess: (response: any, contactId: string) => {
        if (toggleDrawer) toggleDrawer(false);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.contact_deleted')}`,
          });
        if (callback) callback(true, response, contactId);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.contact_deletion')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useBulkDeleteContacts = ({
  setToast,
  toggleDrawer,
  callback,
}: MutationType) => {
  return useMutation(
    async (contactIds: string[]) => {
      if (!contactIds || contactIds.length === 0) return;
      const response = await Promise.all(
        contactIds.map((contactId) =>
          request(`/contacts/${contactId}`, {}, 'DELETE'),
        ),
      );
      return response;
    },
    {
      onSuccess: (response) => {
        if (toggleDrawer) toggleDrawer(false);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.contacts_deleted')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.contacts_deletion')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useExportContacts = ({
  setToast,
  toggleDrawer,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      field_names,
      rule,
    }: {
      field_names: string[];
      rule: RuleType;
    }) => {
      const data = await request(
        qs.stringifyUrl(
          {
            url: '/contacts/export',
            query: {
              ...(field_names ? { field_names } : {}),
            },
          },
          { arrayFormat: 'comma' },
        ),
        rule,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('accountinfo');
        if (toggleDrawer) toggleDrawer(false);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.export_email')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.export_contacts')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useBulkUpdateContacts = ({ setToast, callback }: MutationType) => {
  return useMutation<boolean, unknown, BulkUpdatePayloadType>(
    async ({ contact_ids, fields, rule, type }) => {
      const data: boolean = await request(
        '/contacts/bulk/update',
        { contact_ids, fields, rule },
        type === 'UPDATE' ? 'POST' : 'DELETE',
      );
      return data;
    },
    {
      onSuccess: (response, variables) => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.bulk_update')}`,
          });
        if (callback) callback(true, response, variables);
      },
      onError: (error, variables) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.bulk_update')}`,
          });
        if (callback) callback(false, error, variables);
      },
    },
  );
};
